/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {EventEmitter, Injectable} from '@angular/core';
import {UntypedFormArray, UntypedFormGroup} from '@angular/forms';

import {BehaviorSubject, Observable} from 'rxjs';

import groupBy from 'lodash-es/groupBy';

import * as moment from 'moment';

import {DataTransformer} from './data-transformer/data-transformer';
import {
  BlockingError,
  ErrorMessage,
  FieldEventTrace,
  FormControlWithNativeElement,
  FormGeneratorError,
  FormGeneratorErrorDetail,
  FormGeneratorErrorKind,
  FormValuesWrapper,
  GroupZoneDescriptor,
  GroupZoneType,
  ZoneDescriptor,
  ZoneValues
} from './form-generator.interfaces';
import {FormGeneratorUtils} from './form-generator.utils';

import {DATE_MAX, DATE_MIN} from '../../app.constants';
import {ReportArguments, ReportDefinition} from '../../models/models.interfaces';
import {Template, TemplateArgument} from '../documents/documents.interface';
import {LocalizationService} from '../localization/localization.service';
import {TreeNode} from '../ui';
import {isPresent} from '../utils/lang';
import {at} from '../utils/objects';

/**
 * Form generator service that incapsulate common module logic.
 */
@Injectable()
export class FormGeneratorService {

  private RAW_PREFIX = 'raw:';

  public isActiveCardChanged: EventEmitter<any> = new EventEmitter<any>();
  public alertsRedirectZoneName: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public fieldFocused$: BehaviorSubject<FieldEventTrace> = new BehaviorSubject<FieldEventTrace>(null);
  public fieldBlurred$: BehaviorSubject<FieldEventTrace> = new BehaviorSubject<FieldEventTrace>(null);

  public currentTab$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public clickedTable$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(private localization: LocalizationService) {
  }

  /**
   * Create empty data for given group zone descriptor
   * @param groupZoneDescriptor the group zone descriptor to create empty data for
   * @param id
   * @param markCleared
   * @returns {ZoneValues} the created empty value
   */
  public createEmptyData(groupZoneDescriptor: GroupZoneDescriptor,
                         id?: string,
                         markCleared?: boolean): Array<ZoneValues> {
    const created = this.createEmptyDataInternal(groupZoneDescriptor, id, markCleared);
    return [created];
  }

  private createEmptyDataInternal(groupZoneDescriptor: GroupZoneDescriptor,
                                  id?: string,
                                  markCleared?: boolean): ZoneValues {
    const created = <ZoneValues>{
      id,
      isDated: groupZoneDescriptor.isDated,
      groupName: groupZoneDescriptor.groupZoneID,
      begindate: DATE_MIN,
      enddate: DATE_MAX,
      values: groupZoneDescriptor.zones
        .filter(zone => DataTransformer.zoneTypeHasData(zone.type))
        .reduce((memo, zone) => {
          memo[zone.zoneID] = this.createZoneValue(zone, markCleared);
          return memo;
        }, {})
    };

    if (markCleared) {
      created.cleared = true;
    }

    return created;
  }

  /**
   * Builds the value of a single zone for an empty record.
   * - When cleared: keep the value empty as today ('').
   * - Otherwise: apply the zone default value when it is prefixed with 'raw:'.
   */
  private createZoneValue(zone: ZoneDescriptor, markCleared?: boolean): any {
    if (markCleared) {
      return {type: zone.type, value: '', cleared: true};
    }
    return {type: zone.type, value: this.rawDefaultValue(zone)};
  }


  /**
   * Returns the zone default value when it is a raw one ('raw:' prefix), '' otherwise.
   */
  private rawDefaultValue(zone: ZoneDescriptor): any {
    const defaultValue = zone.defaultValue;
    if (typeof defaultValue !== 'string' || !defaultValue.startsWith(this.RAW_PREFIX)) {
      return '';
    }
    return this.formatValue(zone, defaultValue.slice(this.RAW_PREFIX.length));
  }

  private formatValue(zone: ZoneDescriptor, value: any): any {
    switch (zone.type) {
      case 'INTEGER': {
        const num = this.toNumber(value);
        return Number.isInteger(num) ? num : '';
      }

      case 'DECIMAL':
      case 'CURRENCY': {
        const num = this.toNumber(value);
        return Number.isFinite(num) ? num : '';
      }

      case 'PERCENTAGE': {
        const num = this.toNumber(value);
        return Number.isFinite(num) ? `${num}%` : '';
      }

      case 'BOOLEAN':
        return String(value).trim().toLowerCase() === 'true';

      default:
        return value;
    }
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || typeof value === 'object') {
      return NaN;
    }
    return Number(value);
  }

  /**
   * Validate dated card entities for gaps and overlaps in timeline
   * @param data list of dated card entities
   * @returns {any} Object with counter data for gaps, overlaps and full overlaps amount
   */
  public getGapsOverlaps(data: any[]): any {
    const counter = {
      gaps: 0,
      overlaps: 0
    };

    data = [...data];

    data.sort((item1, item2) =>
      moment(item1.begindate).startOf('day')
        .diff(moment(item2.begindate).startOf('day'), 'days'));

    let greatestEndDate = data[0].enddate;

    for (let i = 0; i < data.length - 1; i++) {
      const prevItem = data[i];
      const curentItem = data[i + 1];

      const diff = moment(curentItem.begindate).startOf('day')
        .diff(moment(prevItem.enddate).startOf('day'), 'days');

      if (diff !== 1) {
        if (diff < 1) {
          counter.overlaps++;
        } else if (moment(curentItem.begindate).startOf('day')
          .diff(moment(greatestEndDate).startOf('day'), 'days') > 1) {
          counter.gaps++;
        }
      }

      greatestEndDate = moment(curentItem.enddate).startOf('day')
        .diff(moment(prevItem.enddate).startOf('day'), 'days') >= 0
        ? curentItem.enddate : prevItem.enddate;
    }
    return counter;
  }

  public createEmptyGroupZoneDescriptor(groupZoneID, zones?: ZoneDescriptor[]): GroupZoneDescriptor {
    return {
      groupZoneID: groupZoneID,
      transId: groupZoneID,
      groupZoneType: null,
      translate: null,
      metadatas: [],
      displayorder: 1,
      internalType: null,
      nbCol: 1,
      isDated: false,
      zones: zones || [],
      readOnly: false
    };
  }

  public createDescriptorFromArguments(groupZoneID,
                                       rArguments?: Array<ReportArguments> | Array<TemplateArgument>): GroupZoneDescriptor {
    const groupZone = this.createEmptyGroupZoneDescriptor(groupZoneID);
    (rArguments || []).forEach(reportArgument => {
      const zone: ZoneDescriptor = {
        zoneID: '' + reportArgument.argumentName,
        zoneGroup: groupZone.groupZoneID,
        transId: reportArgument.transId,
        isRequired: reportArgument.required,
        displayorder: reportArgument.displayOrder,
        nbColumns: reportArgument.nbColumns,
        nbRows: reportArgument.nbRows,
        type: reportArgument.type,
        hidden: reportArgument.hidden,
        defaultValue: reportArgument.defaultValue,
        metadatas: reportArgument.metadatas,
        width: null,
        height: null,
        internalType: null,
        alignment: null,
        readOnly: reportArgument.readOnly,
        validation: reportArgument.validation,
        regExp: reportArgument.regExp
      };
      groupZone.zones.push(zone);
    });

    return groupZone;
  }

  public transformReportDefinitions(reportDefinitions: Array<ReportDefinition>): FormValuesWrapper {
    const descriptors = new Map<string | number, GroupZoneDescriptor>(),
      zonesValues = new Map<string | number, Array<ZoneValues>>();
    reportDefinitions.forEach(reportDefinition => {
      const groupZone = this.createDescriptorFromArguments(reportDefinition.reportName, reportDefinition.arguments);
      reportDefinition.id = reportDefinition.reportName + reportDefinition.gloRegId;
      descriptors.set(reportDefinition.id, groupZone);
      zonesValues.set(
        reportDefinition.id,
        DataTransformer.createZoneValues(groupZone, this));
    });
    return {
      data: reportDefinitions,
      descriptors,
      zonesValues
    };
  }

  public transformTemplateArguments(templates: Template[]): FormValuesWrapper {
    const descriptors = new Map<string | number, GroupZoneDescriptor>(),
      zonesValues = new Map<string | number, Array<ZoneValues>>();
    templates.forEach(template => {
      const groupZone = this.createDescriptorFromArguments(template.name, template.arguments);
      descriptors.set(template.name, groupZone);
      zonesValues.set(
        template.name,
        DataTransformer.createZoneValues(groupZone, this));
    });
    const enrichedTemplates = templates.map(template => ({
      ...template,
      folder: template?.folder ? template.folder : 'Other'
    }));
    const groups = groupBy(enrichedTemplates, el => at(el, 'folder'));
    return {
      data: Object.keys(groups).map(group => <TreeNode<Template>>{
        text: group,
        value: group,
        selectable: false,
        visible: true,
        children: groups[group].map((template: any) => ({
          text: template.transId,
          value: template.name,
          data: template,
          selectable: true,
          visible: true,
          children: []
        }))
      }),
      descriptors,
      zonesValues
    };
  }

  public getGapsOverlapsBoundaries(data: any[], beginBoundary: string = DATE_MIN, endBoundary: string = DATE_MAX): any {
    const counter = this.getGapsOverlaps(data);
    const range = this.getEarliestLatestDates(data);

    if (moment(range.earliest).isAfter(moment(beginBoundary))) {
      counter.gapsToBegin = 1;
    }

    if (moment(range.latest).isBefore(moment(endBoundary))) {
      counter.gapsToEnd = 1;
    }

    return counter;
  }

  public transformZoneValuesList(datas: ZoneValues[]): void {
    datas.forEach(data => {
      this.transformZoneValues(data);
    });
  }

  public transformZoneValues(data: ZoneValues): void {
    Object.keys(data.values).forEach((zone: string) => {
      const itemData = data.values[zone];

      switch (itemData.type) {
        case 'WORK_CYCLE_TABLE':
          if (isPresent(itemData.value) && Array.isArray(itemData.value)) {
            const emptyIndex: number = itemData.value.findIndex(v => v === undefined);
            if (emptyIndex !== -1) {
              itemData.value = itemData.value.slice(0, emptyIndex);
            }
          }
          break;
        case 'FILE':
          delete itemData.file;
          break;
        case 'PERCENTAGE':
          if (isPresent(itemData.value) && itemData.value !== '' && !isNaN(itemData.value)) {
            itemData.value = this.localization
              .localizeNumber(parseFloat(itemData.value) / 100, 'PERCENT', 'en-US')
              .replace(/\s+/, '');
          } else if (!this.isAlreadyFormattedAsPercentageString(itemData.value)) {
            itemData.value = '';
          }
          break;
        case 'LIST':
          if (itemData.value === true) {
            itemData.value = '';
          }
          break;
        default:
          break;
      }
    });
  }

  public getEarliestLatestDates(data: any[]): { earliest: string, latest: string } {
    data = this.sortDated(data);

    return {
      earliest: data.last().begindate,
      latest: data[0].enddate
    };
  }

  public sortDated(data: any[]): Array<ZoneValues | any> {
    data = data.sort((item1, item2) => moment(item2.begindate).startOf('day')
      .diff(moment(item1.begindate).startOf('day'), 'days'));
    data.forEach((item, index) => item.dataIndex = index);

    return data;
  }

  public isErrorBlocking(key: FormGeneratorErrorKind): boolean {
    switch (key) {
      case FormGeneratorErrorKind.BEGIN_AFTER_END:
      case FormGeneratorErrorKind.KEY_REQUIRED:
      case FormGeneratorErrorKind.KEYS_REQUIRED:
      case FormGeneratorErrorKind.OVERLAPS:
      case FormGeneratorErrorKind.OVERLAPS_WITH_KEY:
      case FormGeneratorErrorKind.ZONE_REGEXP:
      case FormGeneratorErrorKind.INVALID_EMAIL_ADDRESS:
      case FormGeneratorErrorKind.VALIDATION_FAILED:
      case FormGeneratorErrorKind.ONLY_ONE_LINE_ALLOWED_BY_KEY:
      case FormGeneratorErrorKind.BLOCKING_ERROR_ON_ANOTHER_ELEMENT:
      case FormGeneratorErrorKind.WORK_CYCLE_END_DATE_BEFORE_BEGIN_DATE:
        return true;
      case FormGeneratorErrorKind.ONE_LINE_REQUIRED:
      case FormGeneratorErrorKind.ONLY_ONE_LINE_ALLOWED:
      case FormGeneratorErrorKind.GAPS:
      case FormGeneratorErrorKind.GAPS_WITH_KEY:
      case FormGeneratorErrorKind.DATED_BLOCK_FIRST_OCCURENCE_BEGIN_DATE_ETERNITY:
      case FormGeneratorErrorKind.DATED_BLOCK_FIRST_OCCURENCE_BEGIN_DATE_ETERNITY_WITH_KEY:
      case FormGeneratorErrorKind.DATED_BLOCK_LAST_OCCURENCE_END_DATE_ETERNITY:
      case FormGeneratorErrorKind.DATED_BLOCK_LAST_OCCURENCE_END_DATE_ETERNITY_WITH_KEY:
      case FormGeneratorErrorKind.NON_DATED_BLOCK_BEGIN_DATE_ETERNITY:
      case FormGeneratorErrorKind.NON_DATED_BLOCK_END_DATE_ETERNITY:
      case FormGeneratorErrorKind.VALUE_NOT_IN_LIST:
      case FormGeneratorErrorKind.NON_BLOCKING_ERROR_ON_ANOTHER_ELEMENT:
      case FormGeneratorErrorKind.ZONE_REQUIRED:
      default:
        return false;
    }
  }

  public createError(key: string, status: boolean = true, additionalValues: any = {}, zone?: string): FormGeneratorErrorDetail {
    let newError: FormGeneratorErrorDetail = {};

    if ((<Array<string>>Object.values(FormGeneratorErrorKind)).includes(key)) {
      newError = {
        status,
        additionalValues,
        isBlocking: this.isErrorBlocking(key as FormGeneratorErrorKind),
        zone
      };
    } else {
      console.warn('key', key, 'does not exist in FormGeneratorErrors');
      newError = {
        status,
        additionalValues,
        isBlocking: false,
        zone
      };
    }

    return newError;
  }

  public isGroupZoneMandatory(groupZoneType: GroupZoneType): boolean {
    if (groupZoneType in GroupZoneType) {
      return (groupZoneType === GroupZoneType.UNIQUE_MANDATORY || groupZoneType === GroupZoneType.MULTIPLE_MANDATORY);
    } else {
      throw new Error(`${groupZoneType} is not a valid GroupZoneTypes`);
    }

  }

  public isGroupZoneUnique(groupZoneType: GroupZoneType): boolean {
    if (groupZoneType in GroupZoneType) {
      return (groupZoneType === GroupZoneType.UNIQUE_MANDATORY || groupZoneType === GroupZoneType.UNIQUE_NON_MANDATORY);
    } else {
      throw new Error(`${groupZoneType} is not a valid GroupZoneTypes`);
    }
  }

  // We have specific validators like IBAN or BIC,
  // that we all regroup within VALIDATION_FAILED kind
  // for others, the error key is not the same as the translation's one
  public formatErrorKey(errorKey: string): string {
    switch (errorKey) {
      case 'invalidBSB':
      case 'invalidIBAN':
      case 'invalidBIC':
      case 'noEmptyArray':
      case 'invalidSSNFrance':
        return FormGeneratorErrorKind.VALIDATION_FAILED;
      case 'invalidEmailAddress':
        return FormGeneratorErrorKind.INVALID_EMAIL_ADDRESS;
      case 'emailExists':
        return FormGeneratorErrorKind.EMAIL_EXISTS;
      case 'regexp':
        return FormGeneratorErrorKind.ZONE_REGEXP;
      default:
        return errorKey;
    }
  }

  // Return a formatted BlockingError[] from different errorGroup/messages
  // If an previousBlockingErrors is provided, we filter only the new ones
  public getBlockingErrors(globalErrorGroup: FormGeneratorError,
                           globalErrorMessageGroup: ErrorMessage[],
                           tabSheetTransId: string,
                           groupZoneTransId: string,
                           previousBlockingErrors?: BlockingError[]
  ): BlockingError[] {

    const blockingErrors: BlockingError[] = [];
    Object.entries(globalErrorGroup).forEach(([key, value]) => {
      const error = value as FormGeneratorErrorDetail;

      if (previousBlockingErrors && error.isBlocking &&
        !previousBlockingErrors.find(initialError => initialError.errorMessage.text === key)
        || !previousBlockingErrors && error.isBlocking) {

        // Exclude BLOCKING_ERROR_ON_ANOTHER_ELEMENT as it is more an indication than a real error
        // Exclude KEY AND KEYS-REQUIRED because is a key of groupZone with key error and
        // this blocking errors resolution modal is not suitable for this error
        // Exclude OVERLAPS_WITH_KEY only temporary. Fix me in next ticket!!!
        if (key !== FormGeneratorErrorKind.BLOCKING_ERROR_ON_ANOTHER_ELEMENT &&
          key !== FormGeneratorErrorKind.OVERLAPS_WITH_KEY &&
          key !== FormGeneratorErrorKind.KEY_REQUIRED && key !== FormGeneratorErrorKind.KEYS_REQUIRED) {
          blockingErrors.push({
            errorMessage: globalErrorMessageGroup.find(errorMessage => errorMessage.text === key),
            tabSheetTransId: tabSheetTransId,
            groupZoneTransId: groupZoneTransId
          });
        }
      }
    });

    return blockingErrors;
  }

  // If an dataIndex is provided, it's a form-table (UntypedFormArray), otherwise it's a form-card (UntypedFormGroup)
  public getZoneControlById(zoneId: string, group: UntypedFormGroup | UntypedFormArray, dataIndex?: number): FormControlWithNativeElement {
    const zoneName = FormGeneratorUtils.sanitizeZoneName(zoneId);
    let zoneControl: FormControlWithNativeElement;

    if (typeof dataIndex === 'number') {
      zoneControl = group.controls[dataIndex].get(zoneName) as FormControlWithNativeElement;
    } else {
      zoneControl = group.get(zoneName) as FormControlWithNativeElement;
    }

    if (zoneControl) {
      return zoneControl;
    }
    throw new Error(`Zone with id ${zoneId} could not be found in group`);
  }

  public getTransIdFromZoneId(zoneId: string, descriptor: GroupZoneDescriptor): string {
    zoneId = zoneId.replace('§', '.');
    const currentZone = descriptor.zones.find(zone => zone.zoneID === zoneId);

    if (currentZone && currentZone.transId) {
      return currentZone.transId;
    }

    console.warn(`No transId was found for zone with zoneId "${zoneId}". zoneId will be used as translate key`);
    return zoneId;
  }

  // Used for fieldFocused$ / fieldBlurred$ subscriptions in form-card / form-table
  // HandleGroupedCards is for groupedCards in form-card
  public filterEventByZoneGroup(event: FieldEventTrace, descriptor: GroupZoneDescriptor, handleGroupedCards?: boolean) {
    if (!event.descriptor.zoneGroup) {
      return false;
    }
    if (event.descriptor.zoneGroup === descriptor.groupZoneID) {
      return true;
    }
    if (handleGroupedCards) {
      if (event.descriptor.zoneGroup.includes(':') && event.descriptor.zoneGroup.includes('_')) {
        return event.descriptor.zoneGroup.split('_')[0] === descriptor.groupZoneID;
      }
    }

    return false;
  }

  // We check if we are back on the same field and if it has already a blocking error
  // cases :
  // - if we did backToEditMode (we want to keep lastFieldFocused as a safe fallback for cancelChange)
  // - another field can have been focused while we wanted to open modal (then we blur)
  public isSameFieldThanPreviousAndHasError(
    event: FieldEventTrace,
    eventToCheck: FieldEventTrace,
    blockingErrors: BlockingError[],
    dataIndex: number): boolean {

    return (
      eventToCheck && blockingErrors.length && event.descriptor.zoneID === eventToCheck.descriptor.zoneID
      && eventToCheck.dataIndex === dataIndex
    );
  }

  private isAlreadyFormattedAsPercentageString(value: any): boolean {
    return value && value.length >= 2 && value[value.length - 1] === '%';
  }

  public updateZoneName(zoneName: string) {
    this.alertsRedirectZoneName.next(zoneName);
  }

  public getZoneNameObservable(): Observable<string> {
    return this.alertsRedirectZoneName.asObservable();
  }

  public getGroupStructure(groupName: string, values: any = {}): ZoneValues[] {
    return [{
      id: undefined,
      begindate: DATE_MIN,
      isDated: groupName === 'PayrollSet' || groupName === 'FixedTermContractEnd',
      enddate: DATE_MAX,
      groupName,
      values
    }];
  }

}
